import React, { memo, useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import { ChartOptions } from 'chart.js';

import {
  CHART_COMPARE_DASH,
  CHART_NO_ANIMATION,
  chartIntegerTicks,
} from './chartTokens';
import { registerCharts } from './registerCharts';

registerCharts();

const INFLOW = '#16a34a';
const OUTFLOW = '#dc2626';
const LINE = '#6756ad';
const DASH = '—';

/**
 * Столбцы + линия от общей нуля (перенос из `CashFlowReport/charts.tsx`,
 * ТЗ 0023 → ТЗ 0103). Столбцы вниз — просто отрицательные значения.
 *
 * Форматы обязательны: «злотые по умолчанию» были спецификой Cash Flow, а у
 * маркетинга столбцы бывают штуками лидов. Cash Flow передаёт свой `money`
 * явно.
 */
export interface IComboBarLineChartProps {
  labels: string[];
  /** `null` — значения нет (не ноль): столбец не рисуется. */
  inflow: Array<number | null>;
  /** Второй ряд столбцов вниз от нуля. Не передан — ряда нет вовсе. */
  outflow?: number[];
  line: Array<number | null>;
  inflowLabel: string;
  outflowLabel?: string;
  lineLabel: string;
  /** Цвет столбцов; массив — свой у каждого столбца (бледный — частичный). */
  inflowColor?: string | string[];
  /** Приписка к подсказке столбца; `null` — без приписки. */
  inflowNotes?: Array<string | null>;
  lineColor?: string;
  /** `right` — линия на своей правой оси со своим форматом. */
  lineAxis?: 'shared' | 'right';
  /** Формат столбцов и левой оси. */
  barFormat: (value: number) => string;
  /** Формат линии (и правой оси при `lineAxis="right"`). */
  lineFormat: (value: number) => string;
  /** Дополнительная строка подсказки точки («конверсия 12 %»). */
  footerOf?: (index: number) => string | null;
  /**
   * ТЗ 0103: ряд сравнения пунктиром (корзина того же номера); `null` —
   * разрыв. Не передан — ряда нет.
   */
  compareLine?: Array<number | null> | null;
  compareLineLabel?: string;
  /** Чей ряд сравнивается: столбцов (левая ось) или линии (её ось). */
  compareOf?: 'bars' | 'line';
  /** Столбцы — счётчик: только целые отметки левой оси. */
  barInteger?: boolean;
  /** Линия — счётчик: только целые отметки правой оси. */
  lineInteger?: boolean;
  /**
   * Подписи осей (B7): «CRM-лиды» слева цветом столбцов, «продажи» справа
   * цветом линии; не заданы — осей без подписи.
   */
  barAxisTitle?: string;
  lineAxisTitle?: string;
}

export const ComboBarLineChart = memo(
  ({
    labels,
    inflow,
    outflow,
    line,
    inflowLabel,
    outflowLabel = '',
    lineLabel,
    inflowColor = INFLOW,
    inflowNotes,
    lineColor = LINE,
    lineAxis = 'shared',
    barFormat,
    lineFormat,
    footerOf,
    compareLine = null,
    compareLineLabel = '',
    compareOf = 'line',
    barInteger = false,
    lineInteger = false,
    barAxisTitle,
    lineAxisTitle,
  }: IComboBarLineChartProps) => {
    const rightAxis = lineAxis === 'right';
    const compareOnLineAxis = compareOf === 'line' && rightAxis;
    const compareColor =
      compareOf === 'bars' && typeof inflowColor === 'string'
        ? inflowColor
        : lineColor;
    const data = useMemo(
      () => ({
        labels,
        datasets: [
          {
            type: 'bar' as const,
            label: inflowLabel,
            data: inflow,
            backgroundColor: inflowColor,
            borderRadius: 2,
            order: 2,
            yAxisID: 'y',
          },
          ...(outflow
            ? [
                {
                  type: 'bar' as const,
                  label: outflowLabel,
                  data: outflow.map((value) => -Math.abs(value)),
                  backgroundColor: OUTFLOW,
                  borderRadius: 2,
                  order: 2,
                  yAxisID: 'y',
                },
              ]
            : []),
          {
            type: 'line' as const,
            label: lineLabel,
            data: line,
            borderColor: lineColor,
            backgroundColor: lineColor,
            borderWidth: 2.5,
            pointRadius: 3,
            // Без сглаживания: кривая между месяцами рисовала значения,
            // которых не было (повторное ревью 0103, B7).
            tension: 0,
            order: 1,
            yAxisID: rightAxis ? 'y1' : 'y',
          },
          ...(compareLine
            ? [
                {
                  type: 'line' as const,
                  label: compareLineLabel,
                  data: compareLine,
                  borderColor: compareColor,
                  backgroundColor: compareColor,
                  borderWidth: 2,
                  borderDash: CHART_COMPARE_DASH,
                  pointRadius: 2,
                  tension: 0,
                  spanGaps: false,
                  order: 0,
                  yAxisID: compareOnLineAxis ? 'y1' : 'y',
                },
              ]
            : []),
        ],
      }),
      [
        labels,
        inflow,
        outflow,
        line,
        inflowLabel,
        outflowLabel,
        lineLabel,
        inflowColor,
        lineColor,
        rightAxis,
        compareLine,
        compareLineLabel,
        compareColor,
        compareOnLineAxis,
      ],
    );

    const options: ChartOptions<'bar'> = useMemo(
      () => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: CHART_NO_ANIMATION,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { position: 'bottom', labels: { boxWidth: 12 } },
          tooltip: {
            callbacks: {
              // Ряд расходов нарисован вниз, его сырое значение
              // отрицательное — подсказка показывает знак как есть.
              label: (context) => {
                const raw = context.raw as number | null;
                const format =
                  rightAxis && context.dataset.yAxisID === 'y1'
                    ? lineFormat
                    : barFormat;
                // «Нет данных» — прочерк, а не ноль (ТЗ 0088).
                const value = raw === null ? DASH : format(Number(raw));
                const note =
                  context.datasetIndex === 0 && inflowNotes
                    ? inflowNotes[context.dataIndex]
                    : null;
                return note
                  ? `${context.dataset.label}: ${value} (${note})`
                  : `${context.dataset.label}: ${value}`;
              },
              footer: (items) => {
                const [first] = items;
                if (!first || !footerOf) return '';
                return footerOf(first.dataIndex) ?? '';
              },
            },
          },
        },
        scales: {
          y: {
            ...(barAxisTitle
              ? {
                  title: {
                    display: true,
                    text: barAxisTitle,
                    // Массив цветов — у полного (первого) столбика.
                    color: Array.isArray(inflowColor)
                      ? inflowColor[0] ?? INFLOW
                      : inflowColor,
                  },
                }
              : {}),
            ticks: {
              ...chartIntegerTicks(barInteger),
              callback: (value) => barFormat(Number(value)),
            },
          },
          ...(rightAxis
            ? {
                y1: {
                  position: 'right' as const,
                  beginAtZero: true,
                  grid: { drawOnChartArea: false },
                  ...(lineAxisTitle
                    ? {
                        title: {
                          display: true,
                          text: lineAxisTitle,
                          color: lineColor,
                        },
                      }
                    : {}),
                  ticks: {
                    ...chartIntegerTicks(lineInteger),
                    callback: (value: string | number) =>
                      lineFormat(Number(value)),
                  },
                },
              }
            : {}),
        },
      }),
      [
        rightAxis,
        barFormat,
        lineFormat,
        inflowNotes,
        footerOf,
        barInteger,
        lineInteger,
        barAxisTitle,
        lineAxisTitle,
        inflowColor,
        lineColor,
      ],
    );

    return <Chart type="bar" data={data as never} options={options as never} />;
  },
);

ComboBarLineChart.displayName = 'ComboBarLineChart';
